import { useDataQuery } from "@hooks/useData/useDataQuery";
import { ScreenWithPlayingInfo } from "@hooks/useScreens";
import {
  EmitEventFunction,
  ScreenPlayingOptionType,
} from "@hooks/useScreens/types";
import { AuthSession } from "./authClient";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { Playlist } from "types/index";
import { ChangePlaylistResponse, CreateMusicLocationResponse, CreateMusicZoneResponse, CreatePlaylistGroupResponse, DeleteMusicZoneResponse, EditPlaylistGroup, EditPlaylistGroupResponse, FetchMusicNowPlayingResponse, FetchPlaylistMetadataResponse, FetchPlaylistResponse, MusicAuthorizeResponse, PlaylistGroup, PlaylistGroupResponse, UpdateMusicLocationResponse, UpdateMusicZoneResponse } from "../types/MusicApi";
import { CustomError } from "../utils/errors";
import { serverEndpoints } from "./serverEndpoints";
import { authClient } from "./authClient";
import { getPagination, selectData, supabase } from "./serverClient";
import useGzLog from "@hooks/useGzLog";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { useAuth } from "@hooks/useAuth";

export const ScreenWithPlayingNameQuery =
  "*, layout (name), playlists (name, items), asset (name), template(name)";

export const defaultPageSize = 30;

async function musicApiCall(endpoint: string, body?: Record<string, any>) {
  const accessToken = await authClient.getAccessToken();
  const res = await fetch(`${serverEndpoints.musicProxy}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ endpoint, body }),
  });
  return res.json();
}

export const useMusicZone = {
  update() {
    return useMutation({
      async mutationFn(zone: Record<string, any>) {
        const data = await musicApiCall("/api/partner/location/zone/edit", zone) as UpdateMusicZoneResponse;
        if (data.status === "error") throw new CustomError(data.message);
        return data;
      },
    })
  },
  create() {
    return useMutation({
      async mutationFn(zone: Record<string, any>) {
        const data = await musicApiCall("/api/partner/location/zone/add", zone) as CreateMusicZoneResponse;
        if (data.status === "error") throw new CustomError(data.message);
        return data;
      },
    })
  },
  delete() {
    return useMutation({
      async mutationFn(zone: { id: number }) {
        const data = await musicApiCall("/api/partner/location/zone/remove", { id: zone.id }) as DeleteMusicZoneResponse;
        if (data.status === "error") throw new CustomError(data.message);
        return data;
      },
    })
  },
  edit() {
    return useMutation({
      mutationFn: async ({ id, status, name }: { id: number, status: string, name: string }) => {
        const data = await musicApiCall("/api/partner/location/zone/edit", { id, name, status });
        if (data.status === 'failed') throw new Error(data.message);
        return data;
      }
    })
  }
}

export const useMusicLocation = {
  delete() {
    return useMutation({
      async mutationFn(locationId: number) {
        const data = await musicApiCall("/api/partner/location/remove", { id: locationId }) as UpdateMusicLocationResponse;
        if (data.status === "error") throw new CustomError(data.message);
        return data;
      },
    })
  },
  update() {
    return useMutation({
      async mutationFn(location: Record<string, any>) {
        const data = await musicApiCall("/api/partner/location/edit", location) as UpdateMusicLocationResponse;
        if (data.status === "error") throw new CustomError(data.message);
        return data;
      },
    })
  },
  create() {
    return useMutation({
      async mutationFn(location: Record<string, any>) {
        const filteredLocation = Object.fromEntries(
          Object.entries(location).filter(([key]) => ['name', 'status', 'address1', 'address2', 'city', 'state', 'zipcode', 'country'].includes(key))
            .map(([key, value]) => [key, value ?? "NY"])
        );
        const data = await musicApiCall("/api/partner/location/add", filteredLocation) as CreateMusicLocationResponse;
        if (data.status === "error") throw new CustomError(data.message);
        return data;
      },
    })
  },
}

export type UserProfileResponse = NonNullable<Awaited<ReturnType<typeof useUserProfileQuery>>["data"]>

export function useUserProfileQuery(session: AuthSession | null | undefined) {
  const user = session?.user;
  return useDataQuery({
    options: {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
    queryKey: ["user-profile", user?.id],
    // enabled: !!user?.id,
    queryFn: async () => {
      if (!user)
        return {
          status: "unauthenticated",
          user: undefined,
        };

      const { data: _profileData } = await selectData({
        table: "profile",
        columns: "*",
      }).eq("id", user.id).single();

      let profileData = _profileData
      if (!profileData && user?.id) {
        const { data: _profileData, error } = await supabase.from("profile").insert({
          id: user.id,
          email: user.email,
        }).select().single()
        if (error || !_profileData) return { status: "unauthenticated", user: undefined };
        profileData = _profileData
      }

      const isAdmin = profileData?.system_role === "admin" || profileData?.system_permissions?.includes("admin:read_all_organizations")

      const orgProfileQuery = selectData({
        table: "profile_organization",
        columns: `*, profile ( * ), organization ( * )`,
      })
        .or(`invitation_status.is.null, invitation_status.eq.accepted`)

      const { data: orgProfilesData, error: orgProfilesError } = !profileData ? { data: [], error: null } : await (
        isAdmin ? orgProfileQuery :
          orgProfileQuery.eq("user_id", user.id)
      )

      if (orgProfilesError || !orgProfilesData) return;

      const { data, error } = await selectData({
        table: "profile_organization",
        columns: `*, profile ( * ), organization ( * )`,
      })
        .or(`invitation_status.is.null, invitation_status.eq.accepted`)
        .in("org_id", orgProfilesData.map(e => e.org_id))

      if (error || !data) return;

      const organizations = data.map((e) => ({
        ...e.organization,
        id: e.organization?.id ?? "",
        profile: e.profile,
        members: [...new Map(data
          ?.filter((m) => !!m.profile?.id && m.org_id === e.organization?.id)
          .map((m) => [
            m.profile?.id ?? "",
            {
              id: m.profile!.id,
              email: m.profile?.email ?? "",
              ...m,
              created_by: "",
            }
          ])).values()],
      }))

      function getOrgOptions() {
        const allOrganizations = [...new Map(organizations.map(o => [`${o.id}-${o.profile?.id}`, o])).values()]
        const isAdmin = profileData?.system_role === "admin" || profileData?.system_permissions?.includes("admin:read_all_organizations");
        if (isAdmin) {
          return allOrganizations ?? [];
        }

        return allOrganizations.filter(o => o.profile?.id === profileData?.id) ?? [];
      }

      return {
        status: "authenticated",
        user: {
          ...profileData,
          id: profileData?.id ?? user.id ?? "",
          role: profileData?.system_role,
          system_permissions: profileData?.system_permissions ?? [],
          organizations: getOrgOptions(),
        },
      } as const;
    },
  });
}

export function useScreenQuery(screenId?: string) {
  return useDataQuery({
    options: {
      enabled: screenId !== undefined,
    },
    queryKey: ["screen", screenId],
    queryFn: async () => {
      if (!screenId) return null;
      const { data, error } = await selectData({
        table: "screen",
        columns: "*",
      }).eq("id", screenId);
      if (error || !data) return null;
      return data;
    },
  });
}

export const useCustomChannels = {
  authorize: (auth_code: string) => {
    return useMutation({
      async mutationFn({ email, password }: { email: string, password: string }) {
        const json = await musicApiCall("/login/authorize", { auth_code, email, password }) as MusicAuthorizeResponse;
        if (json.status === "success") return json;
        throw new CustomError(json.message);
      },
    })
  },
  chooseAccount() {
    return useMutation({
      async mutationFn(props: { account_id: string, auth_code: string, user_id: string }) {
        const json = await musicApiCall("/login/choose_account", props);
        if (json.status === "error") throw new CustomError(json.message ?? "Something went wrong");
        return null;
      },
    })
  }
}

const musicGroupFetchers = {
  async get() {
    const json = await musicApiCall("/api/partner/listener_groups") as PlaylistGroupResponse;
    if (json.status === "success") return json.listener_groups;
    throw new CustomError(json.message);
  },
  async create(body: Omit<PlaylistGroup, 'id' | 'url'>) {
    const json = await musicApiCall("/api/partner/listener_group/create", body) as CreatePlaylistGroupResponse;
    if (json.status === "success") return json.group;
    throw new CustomError(json.message);
  },
  async edit(body: EditPlaylistGroup) {
    const json = await musicApiCall("/api/partner/listener_group/edit", body) as EditPlaylistGroupResponse;
    if (json.status === "success") return json.group_id;
    throw new CustomError(json.message);
  },
  async delete(body: { id: number }) {
    const json = await musicApiCall("/api/partner/listener_group/remove", body);
    if (json.status === "success") return json;
    // API returns 404 for already-deleted groups — treat as success
    if (json.group_id || json.error?.includes("404")) return { group_id: body.id };
    throw new CustomError(json.message);
  },
};

export const useMusicGroupQuery = {
  get() {
    return useDataQuery({
      queryKey: ["music-groups"],
      queryFn: () => musicGroupFetchers.get(),
    })
  },
  create() {
    return useMutation({
      mutationFn(body: Omit<PlaylistGroup, 'id'>) {
        return musicGroupFetchers.create(body)
      }
    })
  },
  removeFromGroup() {
    return useMutation({
      async mutationFn({ groupId, screenWithSameGroupCount: count }: { groupId: number, screenWithSameGroupCount: number }) {
        if (count > 1)
          return { id: groupId }
        await musicGroupFetchers.delete({ id: groupId })
        return { id: groupId }
      }
    })
  },
  upsert() {
    return useMutation({
      async mutationFn({ currentLocationId, groupId, ...body }: Omit<PlaylistGroup, 'id' | 'url'> & { currentLocationId: number, groupId: number | undefined }) {
        if (!groupId) {
          try {
            await musicGroupFetchers.create(body)
          } catch (_) {
            // ignore
          }
          const getRes = await musicGroupFetchers.get()
          const group = getRes.find((e) => e.locations.includes(currentLocationId))
          if (!group) throw new Error("Something went wrong");
          return group
        }
      }
    })
  },
  edit() {
    return useMutation({
      mutationFn(body: EditPlaylistGroup) {
        return musicGroupFetchers.edit(body)
      }
    })
  },
  remove() {
    return useMutation({
      async mutationFn(body: { id: number }) {
        return musicGroupFetchers.delete(body)
      }
    })
  },
  changePlaylist() {
    return useMutation({
      async mutationFn(body: { group_id: number, playlist_id: number }) {
        const json = await musicApiCall("/api/partner/listener_group/change_playlist", body) as ChangePlaylistResponse;
        if (json.status === "success") return json;
        throw new CustomError(json.message);
      }
    })
  }
}

export const useMusicQuery = {
  changePlaylist() {
    return useMutation({
      async mutationFn({ zone_id, playlist_id }: { zone_id: number, playlist_id: number }) {
        const json = await musicApiCall("/api/partner/location/zone/change_playlist", { zone_id, playlist_id }) as ChangePlaylistResponse;
        if (json.status === "success") return json;
        throw new CustomError(json.message);
      }
    })
  },
  playlists(enabled = true) {
    return useDataQuery({
      queryKey: ["music-playlists"],
      options: {
        staleTime: Infinity,
        enabled,
      },
      queryFn: async () => {
        const musicInfo = await musicApiCall("/api/partner/playlists") as FetchPlaylistResponse;
        if (musicInfo.status === "success") return musicInfo.playlists;
        throw new CustomError(musicInfo.message);
      },
    })
  },
  playlistMetadata(playlistId: number) {
    return useDataQuery({
      queryKey: ["music-playlist-metadata", playlistId],
      options: {
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
      },
      queryFn: async () => {
        const musicInfo = await musicApiCall(`/api/partner/playlist/metadata/${playlistId}`) as FetchPlaylistMetadataResponse;
        if (musicInfo.status === "success") return musicInfo.metadata;
        throw new CustomError(musicInfo.message);
      },
    })
  }
}

export function usePlaylistInfoQuery({ playlistId }: { playlistId: string }) {
  return useDataQuery({
    queryKey: ["playlistInfo", playlistId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("playlists")
        .select("*")
        .eq("id", playlistId)
        .single();

      if (error) {
        return null;
      } else {
        return data;
      }
    },
  });
}

export function useUpdateScreenPlaying(
  userId?: string,
  emitEvent?: EmitEventFunction,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      screenId,
      opt,
      imageFit,
      musicInfos,
      orgId,
    }: {
      screenId: string;
      opt: ScreenPlayingOptionType;
      imageFit?: string;
      musicInfos?: FetchMusicNowPlayingResponse[];
      orgId: string;
    }) => {
      const { data: contentSyncedData } = await supabase
        .from("screen")
        .select("content_synced,location_id")
        .eq("id", screenId).single();

      const contentSynced = contentSyncedData?.content_synced;
      const locationId = contentSyncedData?.location_id;

      const updateData = {
        layout_id: opt.type === "layout" ? opt.id : null,
        asset_id: opt.type === "asset" ? opt.id : null,
        playlist_id: opt.type === "playlist" ? opt.id : null,
        video_id: opt.type === "video" ? opt.id : null,
        template_id: opt.type === "template" ? opt.id : null,
        image_fit: imageFit,
        music_infos: musicInfos || undefined,
      }

      const { data, error } = contentSynced && locationId ?
        await supabase
          .from("screen")
          .update(updateData)
          .eq("location_id", locationId)
          .eq("content_synced", true)
          .select(ScreenWithPlayingNameQuery)
          .returns<ScreenWithPlayingInfo[]>()
        : await supabase
          .from("screen")
          .update(updateData)
          .eq("id", screenId)
          .select(ScreenWithPlayingNameQuery)
          .returns<ScreenWithPlayingInfo[]>();

      if (!data || error) {
        throw error || new Error("Failed to update screen");
      }

      for (const screen of data) {
        emitEvent?.("screenChanged", screen.id);
      }

      return data[0];
    },
    onMutate: async ({ screenId, opt, imageFit, musicInfos, orgId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["screens", orgId] });

      // Snapshot the previous value
      const previousScreens = queryClient.getQueryData<ScreenWithPlayingInfo[]>(["screens", orgId]);

      // Optimistically update to the new value
      queryClient.setQueryData<ScreenWithPlayingInfo[]>(["screens", orgId], (old: any) => {
        if (!old) return [];

        const updatedScreens = old.map((screen: any) => {
          if (screen.id === screenId) {
            return {
              ...screen,
              layout_id: opt.type === "layout" ? opt.id : null,
              asset_id: opt.type === "asset" ? opt.id : null,
              playlist_id: opt.type === "playlist" ? opt.id : null,
              video_id: opt.type === "video" ? opt.id : null,
              template_id: opt.type === "template" ? opt.id : null,
              image_fit: imageFit,
              music_infos: musicInfos || undefined,
            };
          }
          return screen;
        });

        // If content_synced is true, apply the update to all screens in the same location
        const updatedScreen = updatedScreens.find((s: any) => s.id === screenId);
        if (updatedScreen?.content_synced && updatedScreen?.location_id) {
          return updatedScreens.map((screen: any) => {
            if (screen.location_id === updatedScreen.location_id && screen.content_synced) {
              return {
                ...screen,
                layout_id: opt.type === "layout" ? opt.id : null,
                asset_id: opt.type === "asset" ? opt.id : null,
                playlist_id: opt.type === "playlist" ? opt.id : null,
                video_id: opt.type === "video" ? opt.id : null,
                template_id: opt.type === "template" ? opt.id : null,
                image_fit: imageFit,
                music_infos: musicInfos || undefined,
              };
            }
            return screen;
          });
        }

        return updatedScreens;
      });

      return { previousScreens };
    },
    onError: (err, variables, context) => {
      if (context?.previousScreens) {
        queryClient.setQueryData(["screens", variables.orgId], context.previousScreens);
      }
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: ["screens", data?.org_id] });
    },
  });
}

export function usePluginThumbnailsQuery() {
  return useDataQuery({
    queryKey: ["pluginThumbnails"],
    queryFn: async () => {
      const { data, error } = await selectData({
        table: "plugin_thumbnail",
        columns: `*`,
      }).order("created_at", { ascending: false }).order("updated_at", { ascending: false });
      if (error || !data) return;
      return data;
    },
  });
}

export function useUnpairScreenQuery() {
  const { log } = useGzLog()
  const { organization } = useAuth()
  const orgId = organization?.id
  return useDataMutation({
    mutationFn: async ({
      screenId,
      softDelete = false,
    }: {
      screenId: string;
      softDelete: boolean
    }) => {
      const accessToken = await authClient.getAccessToken();

      if (!accessToken) return null;

      const data = await fetch(serverEndpoints.unpairScreen, {
        method: "POST",
        body: JSON.stringify({
          screenId,
          orgId,
          softDelete,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const _json = await data.json();

      return _json;
    },
    queryKeyToInvalidate: ["screens", orgId],
    errorMessage: "Failed to unpair screen",
    optimisticUpdateFn: (oldData: ScreenWithPlayingInfo[] | undefined, { screenId, softDelete }) => {
      if (!oldData) return oldData;
      if (softDelete) {
        return oldData.filter((screen) => screen.id !== screenId);
      }
      return oldData.map((screen) =>
        screen.id === screenId ? { ...screen, is_paired: false } : screen
      );
    },
    options: {
      onSuccess: (data, { softDelete }) => {
        if (softDelete) {
          log({
            eventtype: "ScreenDeleted",
            name: "ScreenDeleted",
            payload: {
              screenId: data?.id ?? "undefined",
              screenName: data?.name ?? "undefined",
              locationId: data?.location_id ?? "undefined",
              locationName: data?.location_name ?? "undefined",
              userId: data?.user_id ?? "undefined",
              organizationId: data?.organization_id ?? "undefined",
            },
          });
        } else {
          log({
            eventtype: "ScreenUnpaired",
            name: "ScreenUnpaired",
            payload: {
              screenId: data?.id ?? "undefined",
              screenName: data?.name ?? "undefined",
              locationId: data?.location_id ?? "undefined",
              locationName: data?.location_name ?? "undefined",
              userId: data?.user_id ?? "undefined",
              organizationId: data?.organization_id ?? "undefined",
            },
          });
        }
      },
      onError: (error, { screenId, softDelete }) => {
        if (softDelete) {
          log({
            eventtype: "ScreenDeleteFailed",
            name: "ScreenDeleteFailed",
            payload: {
              screenId: screenId,
              screenName: error.message,
              locationId: error.message,
              locationName: error.message,
              userId: error.message,
              organizationId: error.message,
              errorMessage: String(error.message),
            },
          });
        } else {
          log({
            eventtype: "ScreenUnpairFailed",
            name: "ScreenUnpairFailed",
            payload: {
              screenId: screenId,
              screenName: error.message,
              errorMessage: String(error?.message || "Unknown error unpairing screen"),
            },
          });
        }
      },
    }
  })
}
